import { Controller } from "@hotwired/stimulus";
import Tags from "bootstrap5-tags";

// Handles voice dictation for task create/update and comment forms.
// All form fields are referenced via Stimulus targets — no hardcoded selectors.
export default class extends Controller {
  static values = { taskId: Number, mode: String, buildingComplexId: String, tenantId: Number };

  static targets = [
    "button",        // mic button (shown only inside native app)
    "nameField",     // task name <input>
    "descriptionField", // container holding the description <trix-editor>
    "duedateField",  // task duedate <input type="date">
    "statusField",   // task status <select>
    "priorityField", // task priority <select>
    "responsibleField",    // responsible_id <select>
    "buildingComplexField",// building_complex_id <select>
    "typeField",     // type_id <select>
    "peopleField",       // people_ids <select multiple> (bootstrap5-tags)
    "personGroupField",  // person_group_ids <select multiple> (bootstrap5-tags)
    "tagField",          // tag_ids <select multiple> (bootstrap5-tags, value = tag name)
    "commentField",      // comment <trix-editor> (action cable rich_text_area)
  ];

  connect() {
    this._messageHandler = this._handleNativeMessage.bind(this);
    window.addEventListener("message", this._messageHandler);
    document.addEventListener("message", this._messageHandler);
    this._showButtonIfAvailable();
  }

  disconnect() {
    window.removeEventListener("message", this._messageHandler);
    document.removeEventListener("message", this._messageHandler);
  }

  // Show the mic button only when running inside the native WebView.
  // window.ondeviceai is injected by React Native on every page load.
  _showButtonIfAvailable() {
    if (!window.ondeviceai) return;
    if (this.hasButtonTarget) this.buttonTarget.classList.remove("d-none");
  }

  openDictation() {
    if (!window.ReactNativeWebView) return;
    window.ReactNativeWebView.postMessage(JSON.stringify({
      action: "voice_dictate_open",
      data: {
        mode:              this.modeValue || "task_update",
        taskId:            this.taskIdValue || null,
        buildingComplexId: this.buildingComplexIdValue || null,
        // Pass the currently active tenant so the native layer always uses the
        // correct tenant even if the store hasn't been refreshed after a switch.
        tenantId:          this.tenantIdValue || null,
      }
    }));
  }

  _handleNativeMessage(event) {
    let parsed;
    try { parsed = JSON.parse(event.data); } catch { return; }
    if (!parsed || parsed.action !== "voice_result") return;

    const data = parsed.data;
    if (!data) return;

    if (this.modeValue === "comment") {
      this._fillComment(data);
    } else {
      this._fillTaskForm(data);
    }
  }

  // ── Task form fill ──────────────────────────────────────────────────────────

  _fillTaskForm(f) {
    if (f.name        && this.hasNameFieldTarget)        this._setValue(this.nameFieldTarget, f.name);
    if (f.description && this.hasDescriptionFieldTarget) this._setTrix(this.descriptionFieldTarget, f.description);
    if (f.duedate     && this.hasDuedateFieldTarget)     this._setValue(this.duedateFieldTarget, f.duedate);
    if (f.status      && this.hasStatusFieldTarget)      this._setValue(this.statusFieldTarget, f.status);
    if (f.priority    && this.hasPriorityFieldTarget)    this._setValue(this.priorityFieldTarget, String(f.priority));

    if (f.responsible_id      && this.hasResponsibleFieldTarget)    this._setValue(this.responsibleFieldTarget, String(f.responsible_id));
    if (f.building_complex_id && this.hasBuildingComplexFieldTarget) this._setValue(this.buildingComplexFieldTarget, String(f.building_complex_id));
    if (f.type_id             && this.hasTypeFieldTarget)            this._setValue(this.typeFieldTarget, String(f.type_id));

    if (f.people_ids?.length       && this.hasPeopleFieldTarget)      this._fillTags(this.peopleFieldTarget,      f.people_ids,      f.people_names);
    if (f.person_group_ids?.length && this.hasPersonGroupFieldTarget)  this._fillTags(this.personGroupFieldTarget, f.person_group_ids, f.person_group_names);
    // Tags store tag names as their value (not IDs), so pass names as values.
    if (f.tag_names?.length        && this.hasTagFieldTarget)          this._fillTags(this.tagFieldTarget,         f.tag_names,       f.tag_names);

    this._submitForm();
  }

  // ── Comment form fill ───────────────────────────────────────────────────────

  _fillComment(f) {
    if (f.comment && this.hasCommentFieldTarget) this._setTrix(this.commentFieldTarget, f.comment);
    this._submitForm();
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  // Submits the closest form — works whether the controller is on the <form>
  // itself or on a wrapper element.
  _submitForm() {
    const form = this.element.tagName === "FORM"
      ? this.element
      : this.element.closest("form");
    form?.requestSubmit();
  }

  // Sets a plain <input> or <select> value and fires change.
  _setValue(el, value) {
    el.value = String(value);
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Finds the <trix-editor> inside a container (or uses the element itself if it
  // is a trix-editor) and loads the text as HTML.
  _setTrix(container, text) {
    const trix = container.tagName === "TRIX-EDITOR"
      ? container
      : container.querySelector("trix-editor");
    if (trix?.editor) trix.editor.loadHTML(`<p>${text}</p>`);
  }

  // Adds items to a bootstrap5-tags enhanced <select multiple> via the Tags
  // instance API so the badge UI renders correctly. Falls back to plain <option>
  // selection when the Tags instance is not available yet.
  // `values` and `labels` are parallel arrays.
  _fillTags(el, values, labels) {
    const inst = typeof Tags?.getInstance === "function" ? Tags.getInstance(el) : null;
    (values || []).forEach((value, i) => {
      const label = labels?.[i] ?? String(value);
      if (inst && typeof inst.addItem === "function") {
        inst.addItem(String(label), String(value));
      } else {
        if (!el.querySelector(`option[value="${value}"]`)) {
          const opt = document.createElement("option");
          opt.value = String(value);
          opt.text  = String(label);
          el.add(opt);
        }
        el.querySelector(`option[value="${value}"]`).selected = true;
      }
    });
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
